import { addDays, endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { parseAsString, useQueryStates } from 'nuqs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTimezone } from '@/lib/org';

const ISO = 'yyyy-MM-dd';
export const todayIn = (tz: string) => formatInTimeZone(new Date(), tz, ISO);

export type PeriodPreset = 'today' | 'week' | 'month' | 'prevMonth';
export function presetRange(preset: PeriodPreset, tz: string): { from: string; to: string } {
  const today = todayIn(tz);
  const d = parseISO(today);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      return { from: format(addDays(d, -6), ISO), to: today };
    case 'month':
      return { from: format(startOfMonth(d), ISO), to: today };
    case 'prevMonth': {
      const p = subMonths(d, 1);
      return { from: format(startOfMonth(p), ISO), to: format(endOfMonth(p), ISO) };
    }
  }
}

/** Período na URL (from/to); padrão: mês atual no fuso da barbearia. */
export function usePeriodParams() {
  const tz = useTimezone();
  const [state, set] = useQueryStates({ from: parseAsString, to: parseAsString }, { history: 'replace' });
  const def = presetRange('month', tz);
  return {
    tz,
    from: state.from ?? def.from,
    to: state.to ?? def.to,
    set: (v: { from?: string; to?: string }) => set({ ...v }),
  };
}

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: '7 dias' },
  { key: 'month', label: 'Mês atual' },
  { key: 'prevMonth', label: 'Mês anterior' },
];

export function PeriodFilter({
  from,
  to,
  tz,
  onChange,
}: {
  from: string;
  to: string;
  tz: string;
  onChange: (v: { from: string; to: string }) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Atalhos de período">
        {PRESETS.map((p) => {
          const r = presetRange(p.key, tz);
          const active = r.from === from && r.to === to;
          return (
            <Button key={p.key} type="button" size="sm" variant={active ? 'default' : 'outline'} aria-pressed={active} onClick={() => onChange(r)}>
              {p.label}
            </Button>
          );
        })}
      </div>
      <div className="grid gap-1">
        <Label htmlFor="period-from" className="text-xs">
          De
        </Label>
        <Input id="period-from" type="date" value={from} max={to} onChange={(e) => e.target.value && onChange({ from: e.target.value, to })} className="w-40" />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="period-to" className="text-xs">
          Até
        </Label>
        <Input id="period-to" type="date" value={to} min={from} onChange={(e) => e.target.value && onChange({ from, to: e.target.value })} className="w-40" />
      </div>
    </div>
  );
}
